import { Box, Typography, CircularProgress } from '@mui/material';
import { ResponsiveLineCanvas } from '@nivo/line';
import React, { useMemo } from 'react';
import { CurveData } from '~/domain/patient/dvh';

export type DVHLineChartProps = {
    data: CurveData[];
    normalizationValueGy: number;
    loading?: boolean;
    expanded?: boolean;
};

const DVHLineChart: React.FC<DVHLineChartProps> = ({
    data,
    normalizationValueGy,
    loading = false,
    expanded = false,
}) => {
    // Responsive font sizes based on expanded state
    const tickFontSize = expanded ? 11 : 9;
    const legendFontSize = expanded ? 12 : 10;
    const getMax = (curves: Array<CurveData>) => {
        let max = -Infinity;
        for (const curve of curves) {
            for (const val of curve.data) {
                max = val.x > max ? val.x : max;
            }
        }
        return max;
    };

    const maxXValue = useMemo(
        () => Math.max(getMax(data), normalizationValueGy),
        [data, normalizationValueGy]
    );

    const chartData = useMemo(
        () =>
            data.map((curve) => ({
                id: curve.name,
                data: curve.data, // already downsampled
                color: curve.color,
            })),
        [data]
    );

    const cursorPointToRelativeDose = (point: string | number): string => {
        if (point !== undefined) {
            const pointValue =
                typeof point === 'string' ? parseFloat(point) : point;
            return `${((100 * pointValue) / normalizationValueGy).toFixed(1)}`;
        }
        return '';
    };

    const idToDoseStats = (id: string): string => {
        const dvh = data.find(
            (curve) => curve.name === chartIdToStructureId(id)
        );
        if (dvh) {
            return `Max: ${dvh.doseMaxGy.toFixed(
                1
            )} Gy, Min: ${dvh.doseMinGy.toFixed(
                1
            )} Gy, Mean: ${dvh.doseMeanGy.toFixed(1)} Gy`;
        }
        return '';
    };

    const chartIdToStructureId = (id: string): string => {
        const delimiterIndex = id.lastIndexOf('.');
        if (delimiterIndex !== -1) {
            return id.slice(0, delimiterIndex);
        }
        return id;
    };

    return (
        <Box position="relative" width="100%" height="100%">
            <ResponsiveLineCanvas
                data={chartData}
                margin={{ top: 60, right: 30, bottom: 50, left: 60 }}
                xScale={{ type: 'linear', min: 0, max: maxXValue }}
                yScale={{ type: 'linear', min: 0, max: 100 }}
                data-testid="dvh-line-chart"
                axisRight={null}
                enableGridX={true}
                enableGridY={true}
                lineWidth={1.5}
                enablePoints={false}
                curve="linear"
                enableArea={chartData.length <= 2 && true}
                areaOpacity={0.5}
                theme={{
                    axis: {
                        ticks: {
                            line: {
                                stroke: '#555555',
                            },
                            text: {
                                fill: '#FFFFFF',
                                fontSize: tickFontSize, // 9px for small view, 11px when expanded
                            },
                        },
                        legend: {
                            text: {
                                fill: '#aaaaaa',
                                fontSize: legendFontSize, // 10px for small view, 12px when expanded
                            },
                        },
                    },
                    crosshair: {
                        line: {
                            stroke: '#ffffff',
                        },
                    },
                }}
                axisBottom={{
                    legend: 'Dose (Gy)',
                    legendOffset: 35,
                    legendPosition: 'middle',
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                }}
                axisTop={{
                    legend: 'Relative Dose (%)',
                    legendOffset: -40,
                    legendPosition: 'middle',
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                    format: (value) =>
                        `${((value / normalizationValueGy) * 100).toFixed(1)}%`,
                }}
                axisLeft={{
                    legend: 'Ratio of Total Structure Volume (%)',
                    legendOffset: -40,
                    legendPosition: 'middle',
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                }}
                colors={(chartDataObj) => chartDataObj.color}
                tooltip={({ point }) => {
                    const structureName = chartIdToStructureId(point.id);
                    const doseValue =
                        typeof point.data.x === 'number'
                            ? `${point.data.x.toFixed(1)} Gy`
                            : `${point.data.xFormatted} Gy`;
                    const volumeValue =
                        typeof point.data.y === 'number'
                            ? `${point.data.y.toFixed(1)} %`
                            : `${point.data.yFormatted} %`;
                    const backgroundColor = point.serieColor;
                    const relativeDoseInPercent = cursorPointToRelativeDose(
                        point.data.xFormatted
                    );
                    const tooltipLeaningDir =
                        parseFloat(relativeDoseInPercent) < 50
                            ? 'left'
                            : 'right';
                    const shouldFlipUp =
                        typeof point.data.y === 'number' && point.data.y < 40;
                    return (
                        <Box
                            sx={{
                                background: 'black',
                                padding: '10px',
                                borderRadius: '4px',
                                display: 'flex',
                                flexDirection: 'column',
                                fontSize: '8px',
                                position: 'absolute',
                                minWidth: '360px',
                                [tooltipLeaningDir]: '1em',
                                transform: shouldFlipUp
                                    ? 'translateY(-100%)'
                                    : undefined,
                            }}
                            data-testid="dvh-tooltip"
                        >
                            <Box
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                <Box
                                    style={{
                                        width: '12px',
                                        height: '12px',
                                        marginRight: '5px',
                                        backgroundColor,
                                    }}
                                />
                                <Typography sx={{ fontWeight: 'bold' }}>
                                    {structureName}
                                </Typography>
                            </Box>
                            <Typography>
                                {`Dose: ${doseValue} (${relativeDoseInPercent} %)`}
                            </Typography>
                            <Typography>{`Volume: ${volumeValue}`}</Typography>
                            <Typography>{idToDoseStats(point.id)}</Typography>
                        </Box>
                    );
                }}
            />
            {loading && (
                <Box
                    data-testid="dvh-loading-overlay"
                    position="absolute"
                    top={0}
                    left={0}
                    width="100%"
                    height="100%"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                        pointerEvents: 'none',
                        background:
                            'radial-gradient(circle at center, rgba(0,0,0,0.35), rgba(0,0,0,0.55))',
                        transition: 'opacity 0.3s',
                    }}
                >
                    <CircularProgress
                        size={64}
                        thickness={4}
                        sx={{ color: '#ffffff', opacity: 0.9 }}
                    />
                </Box>
            )}
        </Box>
    );
};

export default DVHLineChart;
